// ── Admin selection / drag chrome ──
// The green outline (plus, in resize mode, the corner handle) drawn on an admin-selected
// map object. Two callers draw the SAME box with different LIFETIMES:
//   • drawAdminSelectionOutline — persistent chrome: survives the gesture and records the
//     marker in `scene.admin.selectedMarker` so clearAdminSelection can find it later.
//   • drawAdminDragOutline      — transient chrome: owned by one drag gesture, destroyed
//     by that gesture's cleanup.
// Both used to inline their own copy of the measure/rect/handle code in
// useMapAdminInteraction. The geometry is measured once here; the two REMOVAL policies
// stay separate on purpose because they genuinely differ (documented at each function).
import type { MutableRefObject } from 'react';
import * as PIXI from 'pixi.js';
import type { MapSceneState } from '../mapSceneState';

// 3px of air between sprite and outline; the handle straddles the outline's top-right corner.
const OUTLINE_PADDING = 3;
const OUTLINE_COLOR = 0x00FF00;
const RESIZE_HANDLE_SIZE = 10;

/** The marker's sprite box in marker-local coordinates. */
export interface MarkerSpriteBox {
  sprite: PIXI.Sprite;
  spriteWidth: number;
  spriteHeight: number;
  leftXLocal: number;
  topYLocal: number;
}

/**
 * Measures the marker's sprite (ignoring Graphics overlays) in marker-local space.
 * `null` means there is nothing to outline; every caller treats that as "draw nothing".
 */
export function measureMarkerSpriteBox(marker: PIXI.Container): MarkerSpriteBox | null {
  const sp = marker.children.find(c => c instanceof PIXI.Sprite) as PIXI.Sprite | undefined;
  if (!sp || !sp.texture) return null;
  const spriteWidth = Math.abs(sp.width);
  const spriteHeight = Math.abs(sp.height);
  const anchorX = sp.anchor?.x ?? 0.5;
  const anchorY = sp.anchor?.y ?? 0.5;
  const centerXLocal = -(anchorX - 0.5) * spriteWidth;
  const centerYLocal = -(anchorY - 0.5) * spriteHeight;
  return {
    sprite: sp,
    spriteWidth,
    spriteHeight,
    leftXLocal: centerXLocal - spriteWidth / 2,
    topYLocal: centerYLocal - spriteHeight / 2,
  };
}

// eventMode='none' on both graphics: they are visual only. An outline that accepts pointer
// events breaks PIXI's pointer tracking when it is destroyed mid-pointerdown (it fires a
// pointercancel, which kills the long-press timer).
function createOutlineGraphics(box: MarkerSpriteBox): PIXI.Graphics {
  const outline = new PIXI.Graphics();
  outline.rect(
    box.leftXLocal - OUTLINE_PADDING,
    box.topYLocal - OUTLINE_PADDING,
    box.spriteWidth + OUTLINE_PADDING * 2,
    box.spriteHeight + OUTLINE_PADDING * 2,
  );
  outline.stroke({ width: 3, color: OUTLINE_COLOR, alpha: 0.8 });
  outline.zIndex = -1;
  outline.eventMode = 'none';
  return outline;
}

function createResizeHandleGraphics(box: MarkerSpriteBox): PIXI.Graphics {
  const handleX = box.leftXLocal + box.spriteWidth + OUTLINE_PADDING - RESIZE_HANDLE_SIZE / 2;
  const handleY = box.topYLocal - OUTLINE_PADDING - RESIZE_HANDLE_SIZE / 2;
  const handle = new PIXI.Graphics();
  handle.rect(handleX, handleY, RESIZE_HANDLE_SIZE, RESIZE_HANDLE_SIZE);
  handle.fill({ color: OUTLINE_COLOR, alpha: 0.9 });
  handle.zIndex = 10;
  handle.eventMode = 'none';
  return handle;
}

/**
 * Persistent selection chrome. Wipes EVERY Graphics child first — it owns none of them,
 * so any Graphics still attached is a leftover outline. Note the order: the wipe happens
 * even when there is no sprite left to measure (original behaviour).
 */
export function drawAdminSelectionOutline(
  sceneRef: MutableRefObject<MapSceneState>,
  marker: PIXI.Container,
  objectId: number,
): void {
  const oldGraphics = marker.children.filter(c => c instanceof PIXI.Graphics);
  for (const g of oldGraphics) {
    marker.removeChild(g);
    g.destroy();
  }

  const box = measureMarkerSpriteBox(marker);
  if (!box) return;

  const isResize = sceneRef.current.admin.objectModes.get(objectId) === 'resize';
  marker.addChildAt(createOutlineGraphics(box), 0);
  if (isResize) marker.addChild(createResizeHandleGraphics(box));

  sceneRef.current.admin.selectedMarker = { marker, objectId };
}

/** Transient chrome owned by a single drag gesture. */
export interface AdminDragOutline {
  outline: PIXI.Graphics;
  resizeHandle: PIXI.Graphics | null;
}

/**
 * Redraws the drag chrome: destroys `previous` (which this module created) and returns the
 * replacement. Returns `null` when the marker has no measurable sprite — and in that case
 * destroys NOTHING, so the caller keeps what it already had. That ordering is deliberate:
 * the original checked the sprite BEFORE removing the old visuals.
 */
export function drawAdminDragOutline(
  marker: PIXI.Container,
  isResizeMode: boolean,
  previous: AdminDragOutline | null,
): AdminDragOutline | null {
  const box = measureMarkerSpriteBox(marker);
  if (!box) return null;

  destroyAdminDragOutline(marker, previous);

  const outline = createOutlineGraphics(box);
  marker.addChildAt(outline, 0);

  let resizeHandle: PIXI.Graphics | null = null;
  if (isResizeMode) {
    resizeHandle = createResizeHandleGraphics(box);
    marker.addChild(resizeHandle);
  }
  return { outline, resizeHandle };
}

/** Detaches and destroys drag chrome. No-op for `null`. */
export function destroyAdminDragOutline(
  marker: PIXI.Container,
  chrome: AdminDragOutline | null,
): void {
  if (!chrome) return;
  if (chrome.outline.parent) marker.removeChild(chrome.outline);
  chrome.outline.destroy();
  if (chrome.resizeHandle) {
    if (chrome.resizeHandle.parent) marker.removeChild(chrome.resizeHandle);
    chrome.resizeHandle.destroy();
  }
}
